import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import Header from "../components/Header";
import { sanityClient } from "../utils/sanityClient";

const CATEGORY_STYLES = {
  Announcement: "bg-[#ffd978]/15 text-[#ffd978] border border-[#ffd978]/30",
  Update: "bg-[#ffb56f]/15 text-[#ffb56f] border border-[#ffb56f]/30",
  Event: "bg-[#ff8f6b]/15 text-[#ff8f6b] border border-[#ff8f6b]/30",
  "Patch Notes": "bg-[#009dff]/15 text-[#009dff] border border-[#009dff]/30",
  News: "bg-[#ffd978]/15 text-[#ffd978] border border-[#ffd978]/30",
};

function Badge({ category }) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-black uppercase ${
        CATEGORY_STYLES[category] || CATEGORY_STYLES.Announcement
      }`}
    >
      {category || "News"}
    </span>
  );
}

const portableComponents = {
  types: {
    image: ({ value }) => {
      const imageUrl = value?.asset?.url;

      if (!imageUrl) return null;

      return (
        <img
          src={imageUrl}
          alt={value?.alt || ""}
          className="my-8 w-full rounded-3xl border border-white/10 object-cover"
          draggable="false"
        />
      );
    },
  },
  block: {
    h1: ({ children }) => (
      <h1 className="mb-5 mt-10 text-5xl font-black text-[#ffd978]">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mb-4 mt-10 text-4xl font-black text-[#ffd978]">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-3 mt-8 text-3xl font-black text-[#ffb56f]">
        {children}
      </h3>
    ),
    normal: ({ children }) => (
      <p className="mb-5 text-lg font-bold leading-8 text-white/80">
        {children}
      </p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-6 border-l-4 border-[#ffd978] bg-white/5 p-5 text-xl font-black text-white">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mb-6 ml-6 list-disc space-y-2 text-lg font-bold text-white/80">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="mb-6 ml-6 list-decimal space-y-2 text-lg font-bold text-white/80">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li>{children}</li>,
    number: ({ children }) => <li>{children}</li>,
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-black text-white">{children}</strong>
    ),
    em: ({ children }) => <em className="text-[#ffd978]">{children}</em>,
    link: ({ value, children }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noreferrer"
        className="font-black text-[#ffd978] underline transition hover:text-[#ffb56f]"
      >
        {children}
      </a>
    ),
  },
};

export default function NewsViewer() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [morePosts, setMorePosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPost();
  }, [slug]);

  async function loadPost() {
    setLoading(true);

    try {
      const query = `*[_type == "newsPost" && slug.current == $slug][0] {
        _id,
        title,
        "slug": slug.current,
        overview,
        category,
        publishedAt,
        body,
        "bannerImage": bannerImage.asset->url
      }`;

      const data = await sanityClient.fetch(query, { slug });

      if (data) {
        setPost(data);
        await loadMore(data._id);
      } else {
        setPost(null);
        setMorePosts([]);
      }
    } catch (error) {
      console.error("Failed to load Sanity news post:", error);
      setPost(null);
      setMorePosts([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadMore(currentId) {
    try {
      const query = `*[_type == "newsPost" && _id != $currentId] | order(publishedAt desc)[0...3] {
        _id,
        title,
        "slug": slug.current,
        overview,
        category,
        publishedAt
      }`;

      const data = await sanityClient.fetch(query, { currentId });
      setMorePosts(data || []);
    } catch (error) {
      console.error("Failed to load more Sanity news:", error);
      setMorePosts([]);
    }
  }

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="relative px-6 pb-24 pt-36 md:px-[7%]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,217,120,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(255,143,107,0.16),transparent_38%)]" />

        {loading ? (
          <p className="relative z-10 font-bold text-white/40">
            Loading news...
          </p>
        ) : !post ? (
          <div className="relative z-10 mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/5 p-10 text-center">
            <h1 className="text-5xl font-black text-[#ffd978]">
              News Not Found
            </h1>
            <p className="mt-3 font-bold text-white/50">
              This post does not exist or has not been published.
            </p>
            <a href="/news" className="outfall-button mt-8">
              Back To News
            </a>
          </div>
        ) : (
          <div className="relative z-10 mx-auto max-w-4xl">
            <a
              href="/news"
              className="text-sm font-black uppercase text-[#ffb56f] transition hover:text-[#ffd978]"
            >
              ← Back To News
            </a>

            {post.bannerImage && (
              <img
                src={post.bannerImage}
                alt={post.title}
                className="mt-8 h-[260px] w-full rounded-[32px] border border-white/10 object-cover shadow-[0_0_35px_rgba(255,143,107,0.18)] md:h-[420px]"
                draggable="false"
              />
            )}

            <div className="mt-8">
              <Badge category={post.category} />

              <h1 className="outfall-heading mt-5 text-[clamp(54px,9vw,112px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
                {post.title}
              </h1>

              <p className="mt-5 max-w-3xl text-2xl font-black leading-relaxed text-white/80">
                {post.overview}
              </p>

              <p className="mt-5 text-sm font-bold uppercase text-white/35">
                By Outfall Staff &middot;{" "}
                {post.publishedAt
                  ? new Date(post.publishedAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : ""}
              </p>
            </div>

            <article className="mt-12 rounded-[28px] border border-white/10 bg-white/[0.04] p-6 md:p-10">
              {post.body ? (
                <PortableText
                  value={post.body}
                  components={portableComponents}
                />
              ) : (
                <p className="text-lg font-bold text-white/40">
                  No article content has been added yet.
                </p>
              )}
            </article>

            {morePosts.length > 0 && (
              <section className="mt-16">
                <h2 className="text-4xl font-black text-[#ffd978]">
                  More News
                </h2>

                <div className="mt-6 grid gap-5 md:grid-cols-3">
                  {morePosts.map((item) => (
                    <a
                      key={item._id}
                      href={`/news/${item.slug}`}
                      className="rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:-translate-y-1 hover:border-[#ffd978]/40 hover:bg-white/10"
                    >
                      <Badge category={item.category} />
                      <h3 className="mt-4 text-xl font-black text-white">
                        {item.title}
                      </h3>
                      <p className="mt-2 line-clamp-3 text-sm font-bold text-white/45">
                        {item.overview}
                      </p>
                    </a>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
